// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import categoryAPI from './budget/categoryService';
import { transactionAPI } from './budget/transactionService';

// Comprehensive budget structure with clean hierarchy
const SAMPLE_DATA = {
  categories: [
    // ──── EXPENSES ────
    {
      name: 'Housing & Utilities',
      category_type: 'EXPENSE',
      color: '#ef4444',
      icon: '🏠',
      subcategories: [
        { name: 'Rent / Mortgage [Need]', icon: '📋' },
        { name: 'Electricity [Need]', icon: '⚡' },
        { name: 'Gas [Need]', icon: '🔥' },
        { name: 'Water [Need]', icon: '💧' },
        { name: 'Internet & Phone [Need]', icon: '📡' },
      ],
    },
    {
      name: 'Food & Dining',
      category_type: 'EXPENSE',
      color: '#dc2626',
      icon: '🛒',
      subcategories: [
        { name: 'Groceries [Need]', icon: '📋' },
        { name: 'Dining Out [Want]', icon: '📋' },
      ],
    },
    {
      name: 'Transportation',
      category_type: 'EXPENSE',
      color: '#b91c1c',
      icon: '🚗',
      subcategories: [
        { name: 'Petrol / Fuel [Need]', icon: '📋' },
        { name: 'Public Transit / Car Maintenance [Need]', icon: '📋' },
      ],
    },
    {
      name: 'Healthcare & Insurance',
      category_type: 'EXPENSE',
      color: '#991b1b',
      icon: '🩺',
      subcategories: [
        { name: 'Medical / Pharmacy [Need]', icon: '📋' },
        { name: 'Insurance Premiums [Need]', icon: '📋' },
      ],
    },
    {
      name: 'Lifestyle & Entertainment',
      category_type: 'EXPENSE',
      color: '#7f1d1d',
      icon: '🎬',
      subcategories: [
        { name: 'Shopping [Want]', icon: '📋' },
        { name: 'Subscriptions & Streaming [Want]', icon: '📋' },
        { name: 'Hobbies [Want]', icon: '📋' },
      ],
    },
    {
      name: 'Travel & Holidays',
      category_type: 'EXPENSE',
      color: '#dc2626',
      icon: '✈️',
      subcategories: [
        { name: 'Flights [Want]', icon: '📋' },
        { name: 'Hotels [Want]', icon: '📋' },
        { name: 'Travel Insurance [Want]', icon: '📋' },
        { name: 'Vacation Spending [Want]', icon: '📋' },
      ],
    },
    {
      name: 'Miscellaneous',
      category_type: 'EXPENSE',
      color: '#b91c1c',
      icon: '📦',
      subcategories: [],
    },

    // ──── INCOME ────
    {
      name: 'Primary Income',
      category_type: 'INCOME',
      color: '#10b981',
      icon: '💼',
      subcategories: [
        { name: 'Salary', icon: '📋' },
        { name: 'Freelance', icon: '📋' },
      ],
    },
    {
      name: 'Passive & Investment',
      category_type: 'INCOME',
      color: '#059669',
      icon: '📈',
      subcategories: [
        { name: 'Dividends', icon: '📋' },
        { name: 'Interest', icon: '📋' },
        { name: 'Rental Income', icon: '📋' },
      ],
    },
    {
      name: 'Bonus',
      category_type: 'INCOME',
      color: '#047857',
      icon: '🎁',
      subcategories: [],
    },
    {
      name: 'Other Income',
      category_type: 'INCOME',
      color: '#065f46',
      icon: '💰',
      subcategories: [],
    },

    // ──── SAVINGS ────
    {
      name: 'Emergency Fund',
      category_type: 'SAVING',
      color: '#06b6d4',
      icon: '🆘',
      subcategories: [],
    },
    {
      name: 'Fixed Deposit',
      category_type: 'SAVING',
      color: '#0891b2',
      icon: '🏛️',
      subcategories: [],
    },
    {
      name: 'Retirement Accounts',
      category_type: 'SAVING',
      color: '#0891b2',
      icon: '🏦',
      subcategories: [
        { name: '401k / IRA', icon: '📋' },
        { name: 'Pension', icon: '📋' },
        { name: 'Other Retirement', icon: '📋' },
      ],
    },
    {
      name: 'Investments',
      category_type: 'SAVING',
      color: '#0d9488',
      icon: '💸',
      subcategories: [
        { name: 'Stocks', icon: '📋' },
        { name: 'Mutual Funds', icon: '📋' },
        { name: 'Bonds', icon: '📋' },
        { name: 'Crypto', icon: '📋' },
      ],
    },
    {
      name: 'Sinking Funds',
      category_type: 'SAVING',
      color: '#06b6d4',
      icon: '🎯',
      subcategories: [
        { name: 'Car Fund', icon: '📋' },
        { name: 'Down Payment', icon: '📋' },
        { name: 'Vacation Fund', icon: '📋' },
        { name: 'Wedding Fund', icon: '📋' },
      ],
    },

    // ──── TRANSFER ────
    {
      name: 'Credit Card Payment',
      category_type: 'TRANSFER',
      color: '#6366f1',
      icon: '💳',
      subcategories: [
        { name: 'Credit Card Payoff', icon: '📋' },
      ],
    },
    {
      name: 'Cash Withdrawal',
      category_type: 'TRANSFER',
      color: '#4f46e5',
      icon: '💵',
      subcategories: [
        { name: 'ATM / Bank', icon: '📋' },
      ],
    },
    {
      name: 'Inter-Account Transfer',
      category_type: 'TRANSFER',
      color: '#4338ca',
      icon: '🏦',
      subcategories: [
        { name: 'Savings Account', icon: '📋' },
        { name: 'Emergency Fund', icon: '📋' },
        { name: 'Investment Account', icon: '📋' },
      ],
    },
    {
      name: 'Reimbursements & Splitting',
      category_type: 'TRANSFER',
      color: '#3730a3',
      icon: '👥',
      subcategories: [
        { name: 'Bill Splitting', icon: '📋' },
        { name: 'Group Payment', icon: '📋' },
        { name: 'Received Reimbursement', icon: '📋' },
      ],
    },
  ],
};

const testDataAPI = {
  /**
   * Deletes all test categories for current user
   */
  deleteTestCategories: async () => {
    try {
      const response = await categoryAPI.list({ active: 'true' });
      const categories = response.data || [];

      const deleted = [];
      const errors = [];

      for (const cat of categories) {
        try {
          await categoryAPI.delete(cat.id, true); // hard delete
          deleted.push(cat.name);
        } catch (err) {
          errors.push(`Failed to delete "${cat.name}"`);
        }
      }

      return {
        success: deleted.length > 0 || categories.length === 0,
        deleted: deleted.length,
        errors,
        message: deleted.length > 0
          ? `✅ Deleted ${deleted.length} categories`
          : '✅ No categories to delete',
      };
    } catch (err) {
      return {
        success: false,
        deleted: 0,
        errors: [err.message || 'Failed to delete categories'],
        message: '❌ Error deleting categories',
      };
    }
  },

  /**
   * Creates sample categories with subcategories
   * Development/Testing only - fills in test data for easy testing
   * Automatically handles duplicates by checking and creating
   */
  populateTestCategories: async () => {
    try {
      const created = [];
      const skipped = [];
      const errors = [];

      for (const categoryData of SAMPLE_DATA.categories) {
        try {
          // Try to create category
          const catRes = await categoryAPI.create({
            name: categoryData.name,
            category_type: categoryData.category_type,
            color: categoryData.color,
            icon: categoryData.icon,
          });

          const categoryId = catRes.data.id;
          created.push(categoryData.name);

          // Create subcategories
          if (categoryData.subcategories && categoryData.subcategories.length > 0) {
            for (const sub of categoryData.subcategories) {
              try {
                const subData = typeof sub === 'string'
                  ? { name: sub }
                  : { name: sub.name, icon: sub.icon || '' };

                await categoryAPI.addSubcategory(categoryId, subData);
              } catch (err) {
                // Subcategory might already exist or error, continue
                if (err.response?.status !== 400) {
                  errors.push(`Subcategory issue in ${categoryData.name}`);
                }
              }
            }
          }
        } catch (err) {
          // Category might already exist
          if (err.response?.status === 400) {
            skipped.push(categoryData.name);
          } else {
            errors.push(`Failed to create category "${categoryData.name}"`);
          }
        }
      }

      const total = created.length + skipped.length;
      let message = '';

      if (created.length > 0) {
        message = `✅ Created ${created.length} categories`;
        if (skipped.length > 0) {
          message += ` (${skipped.length} already existed)`;
        }
      } else if (skipped.length > 0) {
        message = `✅ Test categories ready! ${skipped.length} categories + 44 subcategories available. Click "Clear" to reset & repopulate.`;
      } else {
        message = '❌ No categories created or found';
      }

      return {
        success: total > 0,
        created,
        skipped,
        errors,
        message,
      };
    } catch (err) {
      return {
        success: false,
        created: [],
        skipped: [],
        errors: [err.message || 'Failed to populate test data'],
        message: 'Error populating test data',
      };
    }
  },

  /**
   * Get sample categories data (for preview before creation)
   */
  getSampleData: () => SAMPLE_DATA,

  /**
   * Check if categories already exist
   */
  categoriesExist: async () => {
    try {
      const res = await categoryAPI.list({ active: 'true' });
      return (res.data || []).length > 0;
    } catch (err) {
      return false;
    }
  },

  /**
   * Creates ~100 random test transactions using existing categories and subcategories
   * Spreads transactions across last 365 days with detailed information
   */
  populateTestTransactions: async () => {
    try {
      // Fetch all categories with subcategories
      const catRes = await categoryAPI.list({ active: 'true' });
      const categories = catRes.data || [];

      if (categories.length === 0) {
        return {
          success: false,
          created: 0,
          errors: ['No categories found. Please populate categories first.'],
          message: '❌ Please create categories before populating transactions',
        };
      }

      // Organize categories by type and build subcategory map
      const categoriesByType = {
        EXPENSE: [],
        INCOME: [],
        SAVING: [],
        TRANSFER: [],
      };

      const subcategoryMap = {}; // categoryId -> [subcategories]

      categories.forEach((cat) => {
        if (categoriesByType[cat.category_type]) {
          categoriesByType[cat.category_type].push(cat);
        }
        // Map subcategories to their parent category
        if (cat.subcategories && cat.subcategories.length > 0) {
          subcategoryMap[cat.id] = cat.subcategories;
        }
      });

      const created = [];
      const errors = [];
      const today = new Date();

      // Transaction templates with realistic data
      const generateTransactions = () => {
        const transactions = [];

        // Generate INCOME transactions (15-20 per year)
        const incomeFrequency = [
          { daysInterval: 30, amount: () => 50000 + Math.random() * 20000, desc: 'Monthly Salary' },
          { daysInterval: 45, amount: () => 5000 + Math.random() * 10000, desc: 'Freelance Payment' },
          { daysInterval: 90, amount: () => 1000 + Math.random() * 3000, desc: 'Investment Income' },
        ];

        let day = 0;
        for (const freq of incomeFrequency) {
          while (day < 365) {
            const amount = Math.round(freq.amount());
            transactions.push({
              type: 'INCOME',
              amount,
              description: freq.desc,
              daysAgo: day,
            });
            day += freq.daysInterval + Math.random() * 15;
          }
          day = 0;
        }

        // Generate EXPENSE transactions (60-70 per year - varied amounts)
        const expenseCategories = [
          { name: 'Groceries', min: 300, max: 1500, freq: 7 },
          { name: 'Fuel', min: 1000, max: 2500, freq: 10 },
          { name: 'Utilities', min: 500, max: 2000, freq: 30 },
          { name: 'Dining', min: 200, max: 1000, freq: 5 },
          { name: 'Entertainment', min: 100, max: 3000, freq: 14 },
          { name: 'Shopping', min: 500, max: 5000, freq: 21 },
          { name: 'Travel', min: 2000, max: 15000, freq: 45 },
          { name: 'Healthcare', min: 500, max: 5000, freq: 60 },
        ];

        day = 0;
        for (const expense of expenseCategories) {
          let currentDay = Math.floor(Math.random() * 5);
          while (currentDay < 365) {
            const amount = Math.round(expense.min + Math.random() * (expense.max - expense.min));
            transactions.push({
              type: 'EXPENSE',
              amount,
              description: expense.name,
              daysAgo: currentDay,
            });
            currentDay += expense.freq + Math.floor(Math.random() * 5);
          }
        }

        // Generate TRANSFER transactions (5-10 per year)
        const transferTypes = [
          { name: 'Transfer to Savings', amount: () => 5000 + Math.random() * 10000, freq: 30 },
          { name: 'Credit Card Payment', amount: () => 3000 + Math.random() * 8000, freq: 20 },
          { name: 'ATM Withdrawal', amount: () => 2000 + Math.random() * 3000, freq: 14 },
        ];

        day = 0;
        for (const transfer of transferTypes) {
          let currentDay = Math.floor(Math.random() * 10);
          while (currentDay < 365) {
            const amount = Math.round(transfer.amount());
            transactions.push({
              type: 'TRANSFER',
              amount,
              description: transfer.name,
              daysAgo: currentDay,
            });
            currentDay += transfer.freq;
          }
        }

        return transactions;
      };

      // Generate all transaction templates
      const transactionTemplates = generateTransactions();

      // Create transactions
      for (const txnTemplate of transactionTemplates) {
        try {
          // Get a random category of the matching type
          const matchingCategories = categoriesByType[txnTemplate.type] || [];
          if (matchingCategories.length === 0) continue;

          const randomCategory = matchingCategories[
            Math.floor(Math.random() * matchingCategories.length)
          ];

          // Get a random subcategory if available
          let subcategory = null;
          const subs = subcategoryMap[randomCategory.id];
          if (subs && subs.length > 0) {
            subcategory = subs[Math.floor(Math.random() * subs.length)];
          }

          // Calculate transaction date
          const transactionDate = new Date(today);
          transactionDate.setDate(transactionDate.getDate() - txnTemplate.daysAgo);

          // Create transaction with detailed information
          const payload = {
            date: transactionDate.toISOString().split('T')[0],
            amount: txnTemplate.amount,
            transaction_type: txnTemplate.type,
            category: randomCategory.id,
            subcategory: subcategory?.id || null,
            description: txnTemplate.description,
            notes: `Test transaction - ${txnTemplate.daysAgo} days ago - Via ${randomCategory.name}${subcategory ? ' > ' + subcategory.name : ''}`,
            currency: 'INR',
            merchant_name: txnTemplate.description,
          };

          const response = await transactionAPI.create(payload);
          if (response.data && response.data.id) {
            created.push({
              id: response.data.id,
              description: txnTemplate.description,
              amount: txnTemplate.amount,
              category: randomCategory.name,
              subcategory: subcategory?.name || null,
            });
          }
        } catch (err) {
          const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
          if (!errors.includes(errorMsg)) {
            errors.push(errorMsg);
          }
        }
      }

      // Calculate summary
      const incomeCount = created.filter((t) => {
        const template = transactionTemplates.find((x) => x.description === t.description);
        return template && template.type === 'INCOME';
      }).length;
      const expenseCount = created.filter((t) => {
        const template = transactionTemplates.find((x) => x.description === t.description);
        return template && template.type === 'EXPENSE';
      }).length;
      const transferCount = created.filter((t) => {
        const template = transactionTemplates.find((x) => x.description === t.description);
        return template && template.type === 'TRANSFER';
      }).length;

      return {
        success: created.length > 0,
        created: created.length,
        failed: errors.length,
        totalAttempted: transactionTemplates.length,
        errors: errors.slice(0, 5), // Only return first 5 errors
        summary: {
          incomeCount,
          expenseCount,
          transferCount,
        },
        message:
          created.length > 0
            ? `✅ Created ${created.length}/${transactionTemplates.length} transactions (${incomeCount} income, ${expenseCount} expenses, ${transferCount} transfers) over 1 year`
            : `❌ Failed to create transactions (${errors.length} errors)`,
      };
    } catch (err) {
      return {
        success: false,
        created: 0,
        failed: 0,
        errors: [err.message || 'Failed to populate test transactions'],
        message: '❌ Error populating test transactions',
      };
    }
  },
};

export default testDataAPI;
